import React from "react";
import { FormControl, InputGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ColorPicker from "../../../lib/plugins/color-picker/ColorPicker";
import { FONT_FAMILIES, TABLE_TEXT_STYLE_GROUP } from "../../../lib/global/helpers";

const ALIGN_OPTIONS = [
  { value: "left", icon: "align-left", title: "Left" },
  { value: "center", icon: "align-center", title: "Center" },
  { value: "right", icon: "align-right", title: "Right" },
];

const STYLE_BUTTONS = [
  { field: "bold", label: "B", title: "Bold", style: { fontWeight: "bold" } },
  { field: "italic", label: "I", title: "Italic", style: { fontStyle: "italic", fontFamily: "serif" } },
  { field: "underline", label: "U", title: "Underline", style: { textDecoration: "underline" } },
  { transform: "capitalize", label: "Aa", title: "Capitalize Each Word" },
  { transform: "uppercase", label: "AB", title: "UPPERCASE" },
];

// Background, font, size, colour, alignment and B/I/U/Aa/AB for the items
// table header or rows — either the whole-table values (`overrides` is null)
// or one column's overrides on top of them. For a column, settings it hasn't
// changed show the table value with a "table" tag; changed ones get "reset".
export default function TableTextStyleFields({ effective, overrides, onChange, onReset }) {
  const isColumn = !!overrides;
  const isOverridden = (fields) => isColumn && [].concat(fields).some((f) => (
    overrides[f] !== undefined && overrides[f] !== null && overrides[f] !== ""
  ));

  const label = (text, fields) => (
    <div className="table-style-label">
      <span>{text}</span>
      {isColumn && (isOverridden(fields) ? (
        <button
          type="button"
          className="table-style-reset"
          title="Use the whole-table setting again"
          onClick={() => [].concat(fields).forEach((f) => onReset(f))}
        >
          reset
        </button>
      ) : (
        <span className="table-style-inherited" title="Same as the whole table">table</span>
      ))}
    </div>
  );

  const transform = effective.textTransform || "none";

  return (
    <div className="table-style-fields">
      <div className="table-style-field compact-colour-field">
        {label("Background", "backgroundColor")}
        <ColorPicker
          color={effective.backgroundColor || "#ffffffff"}
          callbackFromParents={(e) => onChange("backgroundColor", e.hex)}
        />
      </div>

      <div className="table-style-field compact-colour-field">
        {label("Font Colour", "fontColor")}
        <ColorPicker
          color={effective.fontColor || "#333333ff"}
          callbackFromParents={(e) => onChange("fontColor", e.hex)}
        />
      </div>

      <div className="table-style-field">
        {label("Font", "fontFamily")}
        <select
          className="form-select form-select-sm"
          value={effective.fontFamily || "Inter"}
          style={{ fontFamily: effective.fontFamily || "Inter" }}
          onChange={(e) => onChange("fontFamily", e.target.value)}
        >
          {FONT_FAMILIES.map((font) => (
            <option key={font} value={font} style={{ fontFamily: font }}>{font}</option>
          ))}
        </select>
      </div>

      <div className="table-style-field">
        {label("Size", "fontSize")}
        <InputGroup size="sm">
          <FormControl
            type="number"
            min="1"
            value={effective.fontSize ?? ""}
            onChange={(e) => onChange("fontSize", e.target.value)}
          />
          <InputGroup.Text>pt</InputGroup.Text>
        </InputGroup>
      </div>

      <div className="table-style-field">
        {label("Align", "textAlign")}
        <div className="table-style-segmented" role="radiogroup" aria-label="Text align">
          {ALIGN_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={(effective.textAlign || "left") === opt.value}
              title={opt.title}
              className={(effective.textAlign || "left") === opt.value ? "active" : ""}
              onClick={() => onChange("textAlign", opt.value)}
            >
              <FontAwesomeIcon icon={opt.icon} />
            </button>
          ))}
        </div>
      </div>

      <div className="table-style-field">
        {label("Style", TABLE_TEXT_STYLE_GROUP)}
        <div className="table-style-segmented" aria-label="Text style">
          {STYLE_BUTTONS.map((btn) => {
            const active = btn.field ? !!effective[btn.field] : transform === btn.transform;
            return (
              <button
                key={btn.label}
                type="button"
                aria-pressed={active}
                title={btn.title}
                className={active ? "active" : ""}
                style={btn.style}
                onClick={() => (btn.field
                  ? onChange(btn.field, !active)
                  // Capitalize and uppercase share one setting.
                  : onChange("textTransform", active ? "none" : btn.transform))}
              >
                {btn.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
